import { useState, useRef } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RotateCcw, MapPin } from "lucide-react";
import singlePodImage from "@/assets/single-pod-javits.jpg";
import meetingPodImage from "@/assets/meeting-pod-javits.jpg";

type PodSelection = "single" | "meeting" | null;

const BookJavitsAdvancedRez = () => {
  const [selection, setSelection] = useState<PodSelection>(null);
  const bookingSectionRef = useRef<HTMLDivElement>(null);

  const handleSelection = (type: PodSelection) => {
    setSelection(type);
    setTimeout(() => {
      bookingSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const handleReset = () => {
    setSelection(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const iframeUrls = {
    single: "https://quietcove.wiseingress.com/#/catalog;uid=e6223e8a-83a0-48fc-9cb9-7ebdd760e089;source=link",
    meeting: "https://quietcove.wiseingress.com/#/catalog;uid=0e6b7195-0aef-4226-8acd-cd044dde10a0;source=link",
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="container mx-auto px-4 py-20">
        <div className="max-w-6xl mx-auto">
          {/* Header Section */}
          <div className="text-center mb-12">
            <h1 className="font-montserrat text-3xl md:text-4xl lg:text-5xl font-extrabold text-primary mb-4">
              Reserve a Quiet Corner Pod at Javits Center
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground">
              Choose your workspace type to view real-time availability.
            </p>
          </div>

          {/* Selection Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-12">
            {/* Meeting Pod Card */}
            <Card
              onClick={() => handleSelection("meeting")}
              className={`cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-1 border-2 ${
                selection === "meeting"
                  ? "border-primary ring-2 ring-primary/30"
                  : "border-border hover:border-primary/50"
              }`}
            >
              <CardContent className="p-6 md:p-8">
                <div className="aspect-video rounded-xl mb-6 overflow-hidden">
                  <img 
                    src={meetingPodImage} 
                    alt="Meeting Pod" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <h2 className="font-montserrat text-2xl md:text-3xl font-bold text-foreground mb-3">
                  Meeting Pod
                </h2>
                <p className="text-muted-foreground text-lg">
                  4-Person Occupancy for small team meetings.
                </p>
                <p className="flex items-center justify-center gap-2 mt-3 text-foreground text-sm font-medium">
                  <MapPin className="w-4 h-4 flex-shrink-0" />
                  <span className="text-center">Located in Javits Lower Pavilion near the Starbucks (next to the charging stations)</span>
                  <MapPin className="w-4 h-4 flex-shrink-0" />
                </p>
                <div className="mt-6">
                  <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${
                    selection === "meeting"
                      ? "bg-primary text-primary-foreground"
                      : "bg-primary/10 text-primary"
                  }`}>
                    {selection === "meeting" ? "Selected" : "Click to Select"}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Single Pod Card */}
            <Card
              onClick={() => handleSelection("single")}
              className={`cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-1 border-2 ${
                selection === "single"
                  ? "border-primary ring-2 ring-primary/30"
                  : "border-border hover:border-primary/50"
              }`}
            >
              <CardContent className="p-6 md:p-8">
                <div className="aspect-video rounded-xl mb-6 overflow-hidden">
                  <img 
                    src={singlePodImage} 
                    alt="Single Person Pod" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <h2 className="font-montserrat text-2xl md:text-3xl font-bold text-foreground mb-3">
                  Single Person Pod
                </h2>
                <p className="text-muted-foreground text-lg">
                  Private, soundproof space for focus and calls.
                </p>
                <p className="flex items-center justify-center gap-2 mt-3 text-foreground text-sm font-medium">
                  <MapPin className="w-4 h-4 flex-shrink-0" />
                  <span className="text-center">Located in Javits Lower Pavilion near the Starbucks (next to the charging stations)</span>
                  <MapPin className="w-4 h-4 flex-shrink-0" />
                </p>
                <div className="mt-6">
                  <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${
                    selection === "single"
                      ? "bg-primary text-primary-foreground"
                      : "bg-primary/10 text-primary"
                  }`}>
                    {selection === "single" ? "Selected" : "Click to Select"}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Dynamic Booking Section */}
          {selection && (
            <div ref={bookingSectionRef} className="pt-8">
              <div className="flex justify-center mb-6">
                <Button
                  onClick={handleReset}
                  variant="outline"
                  className="border-primary text-primary hover:bg-primary/10"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Start Over / Change Selection
                </Button>
              </div>
              
              <div className="w-full bg-secondary rounded-2xl p-4 md:p-8">
                <iframe
                  key={selection}
                  src={iframeUrls[selection]}
                  className="w-full rounded-lg"
                  style={{ border: 0, minHeight: "1800px" }}
                  title={`Book a ${selection === "single" ? "Single Person" : "Meeting"} Pod - Javits Center`}
                  scrolling="no"
                />
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BookJavitsAdvancedRez;
